/**
 * Parse a FishPrawnCrab result string into 3 dice values + optional trailing
 * history value (after "@N").
 *
 * Mirrors logic in game_screen.dart:1089-1095 + _parseHistoryResultValue
 * (game_screen.dart:101-117).
 *
 * Examples:
 *   "1,2,3"          → { dice: [1,2,3], historyValue: 0 }
 *   "1,5,3,@9"       → { dice: [1,5,3], historyValue: 9 }
 *   "Fish=1, Prawn=2, Crab=3 @6" → { dice: [1,2,3], historyValue: 6 }
 */

export interface ParsedResult {
  dice: [number, number, number];
  historyValue: number;
}

export function parseResultString(raw: string): ParsedResult | null {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;

  const nums = Array.from(s.matchAll(/\d+/g))
    .map((m) => parseInt(m[0], 10))
    .filter(Number.isFinite);
  if (nums.length < 3) return null;

  const dice: [number, number, number] = [nums[0], nums[1], nums[2]];
  return { dice, historyValue: parseHistoryResultValue(s) ?? 0 };
}

/** Mirrors _parseHistoryResultValue in game_screen.dart:101-117. */
export function parseHistoryResultValue(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;

  const idx = s.lastIndexOf(',@');
  if (idx >= 0 && idx + 2 < s.length) {
    const n = parseFloat(s.substring(idx + 2).trim());
    return Number.isFinite(n) ? n : null;
  }
  const at = s.lastIndexOf('@');
  if (at >= 0 && at + 1 < s.length) {
    const n = parseFloat(s.substring(at + 1).trim());
    return Number.isFinite(n) ? n : null;
  }

  const matches = Array.from(s.matchAll(/-?\d+(?:\.\d+)?/g)).map((m) => m[0]);
  if (matches.length === 0) return null;
  const last = parseFloat(matches[matches.length - 1]);
  return Number.isFinite(last) ? last : null;
}
